import * as React from 'react'

import {
  Box,
  Button,
  CollapsableCard,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import CheckboxInput from './CheckboxInput'
import MinutesInput from './MinutesInput'
import type ToleranceSettingForm from './form'
import { GridBias, ToleranceType } from './form'

export interface Props {
  form: ToleranceSettingForm
  onSubmit: () => void
  showEffectiveAtModal: () => void
}

type ClockInTypes = 'early_clock_ins' | 'late_clock_ins' | 'early_clock_outs' | 'late_clock_outs'

type GridBiasWithoutNone = Exclude<GridBias, 'none'>

type TaxonomyOption = {
  label: string
  value: ToleranceType
}

type GridRoundingBiasOption = {
  label: string
  value: GridBias
}

const taxonomyOptions = (): Array<TaxonomyOption> => {
  return [
    {
      label: i18n.t(
        'time_tracking.settings.tolerance_settings.scheduled_shifts.tolerance_type.values.all_shifts'
      ),
      value: 'all_shifts',
    },
    {
      label: i18n.t(
        'time_tracking.settings.tolerance_settings.scheduled_shifts.tolerance_type.values.boundary_shifts'
      ),
      value: 'boundary_shifts',
    },
  ]
}

const gridRoundingBiasOptions = (): Array<GridRoundingBiasOption> => {
  return [
    {
      label: i18n.t(
        'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.values.none'
      ),
      value: 'none',
    },
    {
      label: i18n.t(
        'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.values.company'
      ),
      value: 'company',
    },
    {
      label: i18n.t(
        'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.values.balanced'
      ),
      value: 'balanced',
    },
    {
      label: i18n.t(
        'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.values.neutral'
      ),
      value: 'neutral',
    },
  ]
}

const getExplanationByType = (
  type: ClockInTypes,
  explanationOptions: Record<string, string>
): Record<GridBiasWithoutNone, string> => {
  switch (type) {
    case 'early_clock_ins':
      return {
        company: i18n.t(
          'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.explanations.early_clock_ins.company',
          explanationOptions
        ),
        balanced: i18n.t(
          'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.explanations.early_clock_ins.balanced',
          explanationOptions
        ),
        neutral: i18n.t(
          'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.explanations.early_clock_ins.neutral',
          explanationOptions
        ),
      }
    case 'late_clock_ins':
      return {
        company: i18n.t(
          'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.explanations.late_clock_ins.company',
          explanationOptions
        ),
        balanced: i18n.t(
          'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.explanations.late_clock_ins.balanced',
          explanationOptions
        ),
        neutral: i18n.t(
          'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.explanations.late_clock_ins.neutral',
          explanationOptions
        ),
      }
    case 'early_clock_outs':
      return {
        company: i18n.t(
          'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.explanations.early_clock_outs.company',
          explanationOptions
        ),
        balanced: i18n.t(
          'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.explanations.early_clock_outs.balanced',
          explanationOptions
        ),
        neutral: i18n.t(
          'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.explanations.early_clock_outs.neutral',
          explanationOptions
        ),
      }
    case 'late_clock_outs':
      return {
        company: i18n.t(
          'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.explanations.late_clock_outs.company',
          explanationOptions
        ),
        balanced: i18n.t(
          'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.explanations.late_clock_outs.balanced',
          explanationOptions
        ),
        neutral: i18n.t(
          'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.explanations.late_clock_outs.neutral',
          explanationOptions
        ),
      }
    default:
      throw new Error('Invalid clock in type')
  }
}

const ScheduledShifts = ({ form, onSubmit, showEffectiveAtModal }: Props) => {
  const onChange = () => {
    form.get('shiftsToleranceEnabled').set(!form.get('shiftsToleranceEnabled').value)
    if (!form.isNewRecord && disableTolerance()) {
      onSubmit()
    }
  }

  const disableTolerance = () => {
    return !form.get('shiftsToleranceEnabled').value
  }

  const isDirty = form.shiftsIsDirty
  const shiftsDataMandatory = form.shiftsIsNull

  const gridRoundingBiasFieldValue = form.get('gridRoundingBias').value
  const biasValue: GridBias =
    gridRoundingBiasFieldValue === 'company' ||
    gridRoundingBiasFieldValue === 'balanced' ||
    gridRoundingBiasFieldValue === 'neutral'
      ? gridRoundingBiasFieldValue
      : 'none'
  const gridRoundingAllowed = hasFeature('dev_time_tracking_grid_rounding')
  const roundingEnabledBias: GridBias[] = ['company', 'balanced', 'neutral']
  const gridRoundingEnabled = roundingEnabledBias.includes(biasValue)

  const enableClockinRoundUp = (type: ClockInTypes) =>
    biasValue === 'company' && type === 'early_clock_ins'

  const explanations = (type: ClockInTypes): string => {
    const explanationOptions = {
      clock_in_before: form.get('clockInBefore').value,
      clock_in_after: form.get('clockInAfter').value,
      clock_out_before: form.get('clockOutBefore').value,
      clock_out_after: form.get('clockOutAfter').value,
    }

    if (enableClockinRoundUp(type) && form.get('clockInRoundUpEnabled').value) {
      return i18n.t(
        'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.explanations.early_clock_ins.balanced',
        explanationOptions
      )
    }

    if (biasValue === 'none') {
      throw new Error('Invalid grid rounding bias')
    }

    return getExplanationByType(type, explanationOptions)[biasValue]
  }

  const types: Array<ClockInTypes> = [
    'early_clock_ins',
    'late_clock_ins',
    'early_clock_outs',
    'late_clock_outs',
  ] as const

  return (
    <Box aria-label="attendance-tolerance-setting-form-scheduled-shifts">
      <CollapsableCard
        open={Boolean(form.get('shiftsToleranceEnabled').value)}
        Card={
          <Box gap="s8">
            <Box flexDirection="row" justifyContent="spaceBetween">
              <Text.Normal weight="medium">
                {i18n.t('time_tracking.settings.tolerance_settings.scheduled_shifts.title')}
              </Text.Normal>
              <TogglerCheckbox
                value={form.get('shiftsToleranceEnabled').value}
                onChange={onChange}
                labelBefore
              />
            </Box>
            <Box paddingRight="s64">
              <Text.Normal color="grey800">
                {i18n.t('time_tracking.settings.tolerance_settings.scheduled_shifts.description')}
              </Text.Normal>
            </Box>
          </Box>
        }
        Content={
          <Box gap="s16">
            <Select
              label={i18n.t(
                'time_tracking.settings.tolerance_settings.scheduled_shifts.tolerance_type.label'
              )}
              field={form.get('toleranceType')}
              options={taxonomyOptions()}
            />
            {gridRoundingAllowed && (
              <Box gap="s8" aria-label="scheduled-shifts-entrance-time-range">
                <Select
                  label={i18n.t(
                    'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.label'
                  )}
                  field={form.get('gridRoundingBias')}
                  options={gridRoundingBiasOptions()}
                />

                {gridRoundingEnabled &&
                  types.map((type) => (
                    <>
                      <Text.Normal weight="normal" size="100" key={type}>
                        {'• ' + explanations(type)}
                      </Text.Normal>
                      <Box>
                        {enableClockinRoundUp(type) && (
                          <CheckboxInput
                            field={form.get('clockInRoundUpEnabled')}
                            label={i18n.t(
                              'time_tracking.settings.tolerance_settings.scheduled_shifts.grid_rounding_bias.enable_clockin_round_up'
                            )}
                          />
                        )}
                      </Box>
                    </>
                  ))}
              </Box>
            )}
              <Box gap="s12" aria-label="scheduled-shifts-exit-time-range">
                <Box gap="s8">
                <Text.Normal weight="medium">
                  {i18n.t('time_tracking.settings.tolerance_settings.scheduled_shifts.entrance')}
                </Text.Normal>
                <Box
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="spaceBetween"
                  width="full"
                  gap="s12"
                >
                  <Box aria-label="scheduled-shifts-entrance-before-input">
                    <MinutesInput
                      label={i18n.t(
                        'time_tracking.settings.tolerance_settings.scheduled_shifts.before'
                      )}
                      minutesFormField={form.get('clockInBefore')}
                      mandatory={shiftsDataMandatory || form.clockInRoundingWithoutRange}
                    />
                  </Box>
                  <Box paddingTop="s24">
                    <Text.Normal color="grey600">–</Text.Normal>
                  </Box>
                  <Box aria-label="scheduled-shifts-entrance-after-input">
                    <MinutesInput
                      label={i18n.t(
                        'time_tracking.settings.tolerance_settings.scheduled_shifts.after'
                      )}
                      minutesFormField={form.get('clockInAfter')}
                      mandatory={shiftsDataMandatory || form.clockInRoundingWithoutRange}
                    />
                  </Box>
                </Box>
              </Box>
              {!gridRoundingEnabled && (
                <CheckboxInput
                  field={form.get('clockInRoundingEnabled')}
                  label={i18n.t('time_tracking.settings.tolerance_settings.scheduled_shifts.round')}
                />
              )}
            </Box>
            <Box gap="s12">
              <Box gap="s8">
                <Text.Normal weight="medium">
                  {i18n.t('time_tracking.settings.tolerance_settings.scheduled_shifts.exit')}
                </Text.Normal>
                <Box
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="spaceBetween"
                  width="full"
                  gap="s12"
                >
                  <Box aria-label="scheduled-shifts-exit-before-input">
                    <MinutesInput
                      label={i18n.t(
                        'time_tracking.settings.tolerance_settings.scheduled_shifts.before'
                      )}
                      minutesFormField={form.get('clockOutBefore')}
                      mandatory={shiftsDataMandatory || form.clockOutRoundingWithoutRange}
                    />
                  </Box>
                  <Box paddingTop="s24">
                    <Text.Normal color="grey600">–</Text.Normal>
                  </Box>
                  <Box aria-label="scheduled-shifts-exit-after-input">
                    <MinutesInput
                      label={i18n.t(
                        'time_tracking.settings.tolerance_settings.scheduled_shifts.after'
                      )}
                      minutesFormField={form.get('clockOutAfter')}
                      mandatory={shiftsDataMandatory || form.clockOutRoundingWithoutRange}
                    />
                  </Box>
                </Box>
              </Box>
              {!gridRoundingEnabled && (
                <CheckboxInput
                  field={form.get('clockOutRoundingEnabled')}
                  label={i18n.t('time_tracking.settings.tolerance_settings.scheduled_shifts.round')}
                />
              )}
            </Box>
            {isDirty && (
              <Box alignItems="flexEnd">
                <Button onClick={() => showEffectiveAtModal()}>{i18n.t('common.submit')}</Button>
              </Box>
            )}
          </Box>
        }
      />
    </Box>
  )
}

export default observer(ScheduledShifts)
